import type { ArrivalReportRow, PerformanceReportRow } from "@/types/reporting";

export interface KpiTotals {
  clicks: number;
  conversions: number;
  cpa: number;
  ctr: number;
  impressions: number;
  projectedVisits: number | null;
  totalSpend: number;
  vcr: number | null;
}

type KpiSummableTotals = Pick<
  KpiTotals,
  "impressions" | "clicks" | "totalSpend" | "conversions"
> & { videoStart: number; videoComplete: number };

// CTR, VCR, and CPA are blended across the whole range — summed
// impressions/clicks/video counts/spend/conversions first, ratio taken once
// at the end — rather than averaging each row's own rate, since averaging
// per-day rates would weight low-volume days the same as high-volume ones.
export const deriveKpiTotals = (
  rows: PerformanceReportRow[],
  arrivalRows: ArrivalReportRow[]
): KpiTotals => {
  const {
    clicks,
    conversions,
    impressions,
    totalSpend,
    videoComplete,
    videoStart,
  } = rows.reduce<KpiSummableTotals>(
    (acc, row) => {
      acc.clicks += row.clicks ?? 0;
      acc.conversions += row.total_conversions ?? 0;
      acc.impressions += row.impressions ?? 0;
      acc.totalSpend += row.total_spend ?? 0;
      acc.videoComplete += row.video_complete ?? 0;
      acc.videoStart += row.video_start ?? 0;
      return acc;
    },
    {
      clicks: 0,
      conversions: 0,
      impressions: 0,
      totalSpend: 0,
      videoComplete: 0,
      videoStart: 0,
    }
  );

  // null = campaign has no Arrival data (empty results array), distinct from 0.
  const projectedVisits =
    arrivalRows.length === 0
      ? null
      : arrivalRows.reduce((sum, row) => sum + (row.projected_visits ?? 0), 0);

  return {
    clicks,
    conversions,
    cpa: conversions > 0 ? totalSpend / conversions : 0,
    ctr: impressions > 0 ? clicks / impressions : 0,
    impressions,
    projectedVisits,
    totalSpend,
    // null = no video data for this campaign set; distinct from 0 (video ran but none completed).
    vcr: videoStart > 0 ? videoComplete / videoStart : null,
  };
};
